import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ReceiptHeader from "./ReceiptHeader";
import ReceiptDetails from "./ReceiptDetails";
import ReceiptSummary from "./ReceiptSummary";
import EditIcon from "../../../assets/EditInvoice-icon.svg";
import downloadIcon from "../../../assets/download-icon.png";
import { useState, useEffect } from "react";
import EditCreditNoteModal, { CreditNoteFormData } from "./EditCreditNoteModal";
import {
  downloadCreditNote,
  editCreditNote,
  saveCreditNote,
} from "../../../api/generateCreditNoteAPI";
import { getCustomerById } from "../../../api/customerAPI";
import moment from "moment";
import CreditNoteReceiptStyles from "./CreditNoteReceiptStyles";
import colors from "../../../theme/Colours";

const CreditNoteReceipt = () => {
  const { id: customerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const previewData = location.state?.previewData;
  console.log("CreditNoteReceipt previewData:", previewData);

  const [modalOpen, setModalOpen] = useState(false);
  const [creditNoteData, setCreditNoteData] = useState(previewData || null);
  const creditNoteNo = creditNoteData?.creditNoteNo || creditNoteData?.id;

  useEffect(() => {
    if (!customerId) return;
    getCustomerById(customerId)
      .then((res) => {
        const data = res?.data?.data;
        if (!data) return;
        const seller = data.seller_details;
        const customerInfo = data.customer || {};
        const addressInfo = data.address || {};
        const assignedSalesRep = data.assignedSalesRep || null;

        const sellerOrg = seller
          ? {
              name: seller.companyName ?? seller.name ?? "",
              cin: seller.cin ?? "",
              pan: seller.pan ?? "",
              gst: seller.gstNumber ?? "",
              registeredAddress: seller.registeredAddress ?? "",
              factoryAddress: seller.factoryAddress ?? "",
              tel: seller.phoneNumber ?? "",
              email: seller.email ?? "",
            }
          : null;

        const sellerBank = seller
          ? {
              accountName: seller.accountName ?? "",
              accountNumber: seller.accountNumber ?? "",
              name: seller.bankName ?? "",
              branchCode: seller.bankBranch ?? "",
              ifscCode: seller.ifscCode ?? "",
            }
          : null;

        const customerMapped = {
          outletName: customerInfo.outletName ?? "",
          clientCode: customerInfo.clientCode ?? "",
          emailId: customerInfo.emailId ?? "",
          phoneNumber: customerInfo.phoneNumber ?? "",
          pincode: customerInfo.pincode ?? "",
          pan: customerInfo.companyPANNumber ?? "",
          gst: customerInfo.companyGSTNumber ?? "",
          gstStateCode:
            customerInfo.gstStateCode ?? addressInfo.gstStateCode ?? "",
          registeredAddress: addressInfo.registeredAddress ?? "",
          licenseName: addressInfo.licenseName ?? "",
          licensePAN: addressInfo.licensePANNumber ?? "",
          licenseGST: addressInfo.licenseGSTNumber ?? "",
          licenseAddress: addressInfo.licenseAddress ?? "",
          assignedSalesRepName: assignedSalesRep?.salesRep?.name ?? "",
          assignedSalesRepEmail: assignedSalesRep?.salesRep?.email ?? "",
        };

        setCreditNoteData((prev: any) => {
          const prevOrg = prev?.org || {};
          const prevBank = prev?.bank || {};
          const prevCustomer = prev?.customer || {};

          const mergedOrg = { ...prevOrg };
          if (sellerOrg) {
            Object.entries(sellerOrg).forEach(([k, v]) => {
              if (v && !prevOrg[k]) mergedOrg[k] = v;
            });
          }
          const mergedBank = { ...prevBank };
          if (sellerBank) {
            Object.entries(sellerBank).forEach(([k, v]) => {
              if (v && !prevBank[k]) mergedBank[k] = v;
            });
          }
          const mergedCustomer = { ...prevCustomer };
          Object.entries(customerMapped).forEach(([k, v]) => {
            if (v && !prevCustomer[k]) mergedCustomer[k] = v;
          });
          return {
            ...prev,
            org: mergedOrg,
            bank: mergedBank,
            customer: mergedCustomer,
          };
        });
      })
      .catch((err) => {
        console.error("Failed to fetch seller details", err);
      });
  }, [customerId]);
  const handleEditConfirm = async (data: CreditNoteFormData) => {
    try {
      const payload = {
        preTaxTotal: data.preTaxTotal ?? 0,
        invoice: data.invoice,
        date: data.date || creditNoteData?.date,
        id: creditNoteNo,
        orderIds: creditNoteData?.orderIds ?? [],
        invoiceNos: creditNoteData?.invoiceNos ?? [],
      };

      const res = await editCreditNote(customerId!, payload);
      console.log("API response:", res);

      setCreditNoteData({
        ...res.data,
        date: data.date,
        creditNoteTowardsInvoice: data.invoice,
      });
      setModalOpen(false);
    } catch (err) {
      console.error("Failed to update credit note", err);
    }
  };

  const handleConfirm = async () => {
    if (!creditNoteData?.preTaxTotal || creditNoteData.preTaxTotal === 0)
      return;

    try {
      const payload = {
        preTaxTotal: creditNoteData.preTaxTotal ?? 0,
        invoice: creditNoteData.creditNoteTowardsInvoice,
        date: creditNoteData.date,
        id: creditNoteNo,
        orderIds: creditNoteData?.orderIds ?? [],
        invoiceNos: creditNoteData?.invoiceNos ?? [],
      };

      const res = await saveCreditNote(customerId!, payload);
      console.log("Save API response:", res);

      navigate(-1);
    } catch (err) {
      console.error("Failed to confirm credit note", err);
    }
  };

  const isConfirmDisabled =
    !creditNoteData?.preTaxTotal ||
    creditNoteData.preTaxTotal === 0 ||
    !creditNoteData?.creditNoteTowardsInvoice ||
    creditNoteData.creditNoteTowardsInvoice.trim() === "";

  const getValidCreditNoteDate = () => {
    const rawDate = creditNoteData?.date;

    if (!rawDate) return "";

    const parsedDate = moment(
      rawDate,
      ["YYYY-MM-DD", "DD/MM/YYYY", "D/M/YYYY"],
      true
    );

    return parsedDate.isValid()
      ? parsedDate.format("YYYY-MM-DD")
      : moment(rawDate).format("YYYY-MM-DD");
  };

  return (
    <Box sx={CreditNoteReceiptStyles.root}>
      <Box sx={CreditNoteReceiptStyles.headerRow}>
        <Typography variant="h1" sx={CreditNoteReceiptStyles.title}>
          Credit Note Receipt
        </Typography>
      </Box>

      <Box sx={CreditNoteReceiptStyles.tableWrapper}>
        <TableContainer
          component={Paper}
          sx={CreditNoteReceiptStyles.tableContainer}
        >
          <Table sx={CreditNoteReceiptStyles.table}>
            <TableHead>
              <TableCell colSpan={2} sx={CreditNoteReceiptStyles.tableHeadCell}>
                <ReceiptHeader previewData={creditNoteData} />
              </TableCell>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={2}
                  sx={CreditNoteReceiptStyles.tableBodyCell}
                >
                  <ReceiptDetails previewData={creditNoteData} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  colSpan={2}
                  sx={CreditNoteReceiptStyles.tableBodyCell}
                >
                  <ReceiptSummary previewData={creditNoteData} />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            sx={{
              padding: "4px 18px",
              backgroundColor: colors.primaryButtonOrange,
              borderRadius: "8px",
              width: "auto",
              alignSelf: "flex-start",
              marginTop: 2,
            }}
            disabled={isConfirmDisabled}
            onClick={handleConfirm}
          >
            Confirm
          </Button>
        </Box>
      </Box>

      <EditCreditNoteModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleEditConfirm}
        initialFormData={{
          preTaxTotal:
            creditNoteData?.preTaxTotal && creditNoteData.preTaxTotal > 0
              ? creditNoteData.preTaxTotal
              : undefined,
          invoice: creditNoteData?.creditNoteTowardsInvoice ?? "",
          date: getValidCreditNoteDate(),
        }}
      />
    </Box>
  );
};

export default CreditNoteReceipt;
